'use client'

import { useDebounce } from '@/hooks'
import { useAccountStore, useCartStore } from '@/store'
import isEmpty from 'lodash/isEmpty'
import { useEffect } from 'react'
import { Control, UseFormReset, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { setEmailToCartGuest } from '@/features/cart'
import yup from '@/utils/yup-global'
import { AddressCode } from '../components/checkout/checkout-info-v2'

interface UseCheckoutFormProps {
  watch: UseFormWatch<IFormCheckout>
  reset: UseFormReset<IFormCheckout>
  setValue: UseFormSetValue<IFormCheckout>
  onSubmitShippingAddress: (data: any) => Promise<void>
  listAddressTypes: TAddressResponse[] | undefined
  getValues: (field?: string) => string
  isExistDistrictField: boolean
  control: Control<IFormCheckout>
}
// Sử dụng Yup để kiểm tra email
const isValidEmail = (email: string): boolean => {
  try {
    // Sử dụng validator email của Yup
    return yup.string().email().isValidSync(email)
  } catch (error) {
    return false
  }
}

export const useCheckoutForm = ({
  watch,
  reset,
  setValue,
  onSubmitShippingAddress,
  listAddressTypes,
  getValues,
  isExistDistrictField,
  control,
}: UseCheckoutFormProps) => {
  const { cartData } = useCartStore()
  const { data: userInfo } = useAccountStore()
  const addressTypeDebounce = useWatch({ control: control, name: 'addressType' })
  const selectedWardCode = useWatch({ control: control, name: AddressCode.WARD })
  const street = useDebounce(useWatch({ control: control, name: 'street' }) as string, 500)
  const emailValue = useDebounce(useWatch({ control: control, name: 'email' }), 500)
  // Reset form when cartData have data
  useEffect(() => {
    const setDefaultFields = () => {
      const address = cartData?.shipping_addresses?.[0]
      const tempEmail = (() => {
        if (emailValue) return emailValue
        if (cartData?.email?.includes(address?.telephone + 'guest')) return ''
        return cartData?.email
      })()
      setValue('name', getValues('name') || (address?.lastname as string))
      setValue('phone', getValues('phone') || address?.telephone)
      setValue('email', tempEmail as string)
      setValue(AddressCode.CITY, String(getValues(AddressCode.CITY) || address?.city_id))
      setValue(AddressCode.DISTRICT, String(getValues(AddressCode.DISTRICT) || address?.district_code))
      setValue(AddressCode.WARD, String(getValues(AddressCode.WARD) || address?.ward_code))
      setValue('rule', Boolean(getValues('rule')))
      setValue('note', getValues('note') || address?.address_note)
    }
    if (!isEmpty(userInfo)) {
      const address = cartData?.shipping_addresses?.[0]
      if (!!userInfo?.addresses?.length) {
        setValue('name', userInfo?.firstname + ' ' + userInfo?.lastname)
        setValue('phone', userInfo?.phone_number || address?.telephone)
        setValue('email', userInfo?.email)
        setValue('rule', Boolean(getValues('rule')))
        setValue(
          AddressCode.CITY,
          String(
            userInfo?.addresses?.[0]?.custom_attributes?.find((el) => el?.attribute_code == AddressCode.CITY)
          )
        )
        setValue(
          AddressCode.DISTRICT,
          String(
            userInfo?.addresses?.[0]?.custom_attributes?.find(
              (el) => el?.attribute_code == AddressCode.DISTRICT
            )
          )
        )
        setValue(
          AddressCode.WARD,
          String(
            userInfo?.addresses?.[0]?.custom_attributes?.find((el) => el?.attribute_code == AddressCode.WARD)
          )
        )
      } else {
        setDefaultFields()
      }
    } else if (!!cartData?.shipping_addresses?.length) {
      setDefaultFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData?.shipping_addresses?.length, userInfo])

  // set default address type
  useEffect(() => {
    if (listAddressTypes?.length && !addressTypeDebounce) {
      setValue('addressType', listAddressTypes[0].type_code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listAddressTypes])

  useEffect(() => {
    if (!userInfo?.addresses?.length && selectedWardCode && !!street) {
      const formData = {
        name: getValues('name'),
        phone: getValues('phone'),
        province_code: getValues('province_code'),
        ward_code: getValues('ward_code'),
        street: getValues('street'),
        district_code: isExistDistrictField ? getValues('district_code') : undefined,
      }

      const { name, phone, province_code, ward_code, street, district_code } = formData

      if (!name || !phone) return

      const isValidAddress = isExistDistrictField
        ? Boolean(province_code && district_code && ward_code && street)
        : Boolean(province_code && ward_code && street)

      if (isValidAddress) {
        onSubmitShippingAddress(getValues())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWardCode, isExistDistrictField, userInfo?.addresses?.length, street])

  useEffect(() => {
    // Kiểm tra nếu người dùng đã đăng nhập hoặc không có email thì không cần xử lý
    if (!isEmpty(userInfo) || !emailValue) return

    // Kiểm tra tính hợp lệ của email trước khi gọi API
    if (!isValidEmail(emailValue as string)) return

    // Chỉ gọi API khi email hợp lệ và khác với email hiện tại trong cart
    if (cartData?.email !== emailValue) {
      setEmailToCartGuest(emailValue as string, cartData?.id)
    }
  }, [cartData?.email, cartData?.id, userInfo, emailValue])

  return {}
}
